import { logUsage, PerformanceDatapoint } from 'owa-analytics';
import type { CloudFile } from 'owa-attachment-file-types';
import {
    addLinkFile,
    clearLinkFiles,
    onDraftEventCreated,
    onIsDraftChanged,
    saveEventDraft,
} from 'owa-calendar-compose-form-actions';
import type AttachmentWellHost from 'owa-calendar-meeting-forms-common-store/lib/store/schema/AttachmentWellHost';
import type { ClientAttachmentId, ClientItemId } from 'owa-client-ids';
import {
    createAttachmentFromLink,
    deleteLink,
    getSharingLinkInfo,
    lazySetLinkActionStatus,
    LinkActionStatus,
    SharingLinkInfo,
    lazyCreateCloudFileFromSharingLink,
} from 'owa-link-data';
import type AttachmentType from 'owa-service/lib/contract/AttachmentType';
import { orchestrator } from 'satcheljs';
import createAttachments from '../actions/createAttachments';
import { getFullComposeViewState, findFormIdByLinkId } from 'owa-calendar-compose-store-manager';
import getEventClientItemId from '../utils/getEventClientItemId';

// set as a global variable so that it can be called sync in tryRecoverLink
// Will always be defined, since tryRecoverLink is only called as a callback for
// createAttachments inside of createAttachmentForLink, which sets it.
let setLinkActionStatus;

// We store the datapoint locally since we start it in response to one action and end
// it in response to a mostly unrelated action, so it can't just be passed through.
let attachmentDatapoints: Map<string, PerformanceDatapoint> = new Map<
    string,
    PerformanceDatapoint
>();

orchestrator(createAttachmentFromLink, async actionMessage => {
    const formId = findFormIdByLinkId(actionMessage.linkId) ?? null;

    // If the event has not been created, store the attachment files
    // details in the compose store and start saving a draft. When
    // the event has been created the onDraftEventCreated action will
    // result in the attachments being created on the event
    const eventId = getEventClientItemId(formId);
    if (!eventId) {
        addLinkFile(actionMessage.linkId, actionMessage.providerId, formId);
        onIsDraftChanged(true /* isDraftEvent */, formId);
        saveEventDraft(formId);

        if (!setLinkActionStatus) {
            setLinkActionStatus = await lazySetLinkActionStatus.import();
        }
        setLinkActionStatus(actionMessage.linkId, LinkActionStatus.pendingAttachAsACopy);

        const datapoint = attachmentDatapoints.get(formId);
        if (!datapoint) {
            const perfDatapoint = new PerformanceDatapoint('AttachLinkAsCopyStartedWithoutDraft');
            attachmentDatapoints.set(formId, perfDatapoint);
        } else {
            datapoint.addCustomData({ secondAttemptMade: true });
        }
        return;
    }

    logUsage('AttachAsACopyStartedWithDraftAlreadySaved');
    // The event has already been created so can add the
    // attachments now.
    createAttachmentFromLinkInternal(
        actionMessage.linkId,
        actionMessage.providerId,
        actionMessage.isTryAgain,
        actionMessage.targetWindow,
        formId
    );
});

orchestrator(onDraftEventCreated, async actionMessage => {
    const { formId } = actionMessage;
    // If the user has not yet picked any link(s) to be added
    // as attachment(s), or there is not a full compose open there is nothing to do
    const fullComposeViewState = getFullComposeViewState(formId);
    if (!fullComposeViewState || !fullComposeViewState.linkFiles) {
        const datapoint = attachmentDatapoints.get(formId);
        if (datapoint) {
            datapoint.addCustomData({ createdAttachments: false });
            datapoint.end();
            attachmentDatapoints.delete(formId);
        }
        return;
    }

    // The event has already been created so can add the
    // attachments now.
    fullComposeViewState.linkFiles.map(file => {
        createAttachmentFromLinkInternal(
            file.linkId,
            file.providerId,
            false /* isTryAgain */,
            null,
            formId
        );
    });
    clearLinkFiles(formId);

    const datapoint = attachmentDatapoints.get(formId);
    if (datapoint) {
        datapoint.addCustomData({ createdAttachments: true });
        datapoint.end();
        attachmentDatapoints.delete(formId);
    }
});

async function createAttachmentFromLinkInternal(
    linkId: string,
    providerId: string,
    isTryAgain: boolean,
    targetWindow: Window,
    formId: string
) {
    const composeViewState = getFullComposeViewState(formId) as AttachmentWellHost;
    const sharingLink: SharingLinkInfo = getSharingLinkInfo(linkId);
    if (composeViewState && sharingLink) {
        if (!setLinkActionStatus) {
            setLinkActionStatus = await lazySetLinkActionStatus.import();
        }
        setLinkActionStatus(linkId, LinkActionStatus.attachingAsACopy);

        const createCloudFileFromSharingLink = await lazyCreateCloudFileFromSharingLink.import();
        const cloudFile: CloudFile = createCloudFileFromSharingLink(sharingLink, providerId);
        createAttachments(
            composeViewState,
            getEventClientItemId(formId),
            [cloudFile],
            {
                isInline: false,
                isHiddenInline: false,
                shouldShare: false,
                downsellFromLink: true,
            },
            formId,
            null /* infoBar /*/,
            null /* onAllAttachmentsProcessed /*/,
            (
                parentItemId: ClientItemId,
                attachmentId: ClientAttachmentId,
                attachment: AttachmentType
            ) => tryDeleteLink(linkId, isTryAgain, targetWindow) /* onAttachmentCreatedCallback */,
            (canceled: boolean) =>
                tryRecoverLink(
                    linkId,
                    canceled,
                    isTryAgain
                ) /* onAttachmentCanceledOrFailedCallback */,
            () => {
                logUsage('LinkAttachAsCopyTriedAgain', { isCalendar: true });
                createAttachmentFromLink(
                    linkId,
                    providerId,
                    true /* isTryAgain */,
                    targetWindow
                ); /* tryAgainCallback */
            }
        );
    }
}

// Deletes the link in the body after a copy is attached
function tryDeleteLink(linkId: string, tryAgian: boolean, targetWindow: Window) {
    // We put this in a set timeout in case the user cancels the attachment at the last second.
    // In this case we will get a cancel call back right after the attach call back, which will have changed the
    // actionStatus to let us know the attachment wasn't actually created. The window in which a user can do this
    // is larger than you would think.
    logUsage('linkAttachAsACopySuccess', { isTryAgain: tryAgian });
    setTimeout(() => {
        const sharingLink: SharingLinkInfo = getSharingLinkInfo(linkId);
        if (sharingLink && sharingLink.linkActionStatus === LinkActionStatus.attachingAsACopy) {
            deleteLink(linkId, targetWindow);
        }
    }, 100);
}

// Moves the link in the body back to its normal state after attachAsACopy fails or is canceled
function tryRecoverLink(linkId: string, canceled: boolean, tryAgain: boolean) {
    setLinkActionStatus(linkId, LinkActionStatus.none);
    if (canceled) {
        logUsage('linkAttachAsACopyCanceled', { isTryAGain: tryAgain, isCalendar: true });
    } else {
        logUsage('linkAttachAsACopyFailed', { isTryAgain: tryAgain, isCalendar: true });
    }
}
